import {
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatRect } from "components/DocumentMapping/pdfFieldGeometry";
import {
  ADMISSION_PREFILL_PREFIX,
  AUTOFILL_OWNER,
  FIELD_TYPES,
  NO_PREFILL_VALUE,
  OWNERS,
} from "./mappingConstants";

const REQUIRED_ELIGIBLE_OWNER = "CLIENT";

/** Field type select, locked once a prefill key is set, since the catalog owns the type then. */
const FieldTypeSelect = ({ field, canEdit, onChange }) => (
  <TextField
    select
    size="small"
    label="Type"
    fullWidth
    value={field.fieldType}
    disabled={!canEdit || Boolean(field.prefillKey)}
    onChange={(event) => onChange({ fieldType: event.target.value })}
  >
    {FIELD_TYPES.map((type) => (
      <MenuItem key={type} value={type}>
        {type}
      </MenuItem>
    ))}
  </TextField>
);

/** Who fills the field in. Locked to AUTOFILLED once linked; unlink first to hand it back. */
const FieldOwnerSelect = ({ field, canEdit, onChange }) => {
  const options = field.prefillKey ? [...OWNERS, AUTOFILL_OWNER] : OWNERS;
  return (
    <TextField
      select
      size="small"
      label="Filled by"
      fullWidth
      value={field.prefillKey ? AUTOFILL_OWNER : field.signerRole}
      disabled={!canEdit || Boolean(field.prefillKey)}
      onChange={(event) => onChange({ signerRole: event.target.value })}
    >
      {options.map((owner) => (
        <MenuItem key={owner} value={owner}>
          {owner}
        </MenuItem>
      ))}
    </TextField>
  );
};

/** Admission-linked fields are the one source that may resolve to nothing without blocking a
 * send, so the mapping screen says so here rather than letting it surprise whoever sends. */
const AdmissionPrefillNote = ({ field }) =>
  field.prefillKey?.startsWith(ADMISSION_PREFILL_PREFIX) ? (
    <FormHelperText sx={{ mt: 0.5 }}>
      Filled from the linked admission. If no admission is picked when sending, you&apos;ll be asked
      to type this in.
    </FormHelperText>
  ) : null;

const PrefillKeySelect = ({ field, canEdit, prefillGroups, onPrefillChange }) => (
  <TextField
    select
    size="small"
    fullWidth
    sx={{ mt: 1 }}
    label="Auto-fill from"
    value={field.prefillKey || NO_PREFILL_VALUE}
    disabled={!canEdit}
    onChange={(event) => onPrefillChange(event.target.value || null)}
  >
    <MenuItem value={NO_PREFILL_VALUE}>Not linked (filled in manually)</MenuItem>
    {prefillGroups.flatMap((group) => [
      <MenuItem key={group.source} disabled sx={{ opacity: 0.7 }}>
        {group.label}
      </MenuItem>,
      ...group.keys.map((key) => (
        <MenuItem key={key.key} value={key.key} sx={{ pl: 3 }}>
          {key.label}
        </MenuItem>
      )),
    ])}
  </TextField>
);

/** One field's editable row: label, type/owner, prefill link, required flag and its rectangle. */
const MappingFieldCard = ({
  field,
  isSelected,
  canEdit,
  prefillGroups,
  onSelect,
  onUpdate,
  onPrefillChange,
  onDelete,
}) => (
  <Card
    variant="outlined"
    sx={{ p: 1, mb: 1, borderColor: isSelected ? "primary.main" : "divider" }}
    onClick={onSelect}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <TextField
        size="small"
        variant="standard"
        value={field.label}
        disabled={!canEdit}
        onChange={(event) => onUpdate({ label: event.target.value })}
      />
      {canEdit && (
        <IconButton size="small" onClick={onDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>

    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
      <FieldTypeSelect field={field} canEdit={canEdit} onChange={onUpdate} />
      <FieldOwnerSelect field={field} canEdit={canEdit} onChange={onUpdate} />
    </Stack>

    <PrefillKeySelect
      field={field}
      canEdit={canEdit}
      prefillGroups={prefillGroups}
      onPrefillChange={onPrefillChange}
    />
    <AdmissionPrefillNote field={field} />

    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={Boolean(field.required)}
            disabled={!canEdit || field.signerRole !== REQUIRED_ELIGIBLE_OWNER}
            onChange={(event) => onUpdate({ required: event.target.checked })}
          />
        }
        label="Required"
      />
      <Chip size="small" label={`p${field.pageNumber} · ${formatRect(field)}`} />
    </Stack>
  </Card>
);

export default MappingFieldCard;
